// Pure logic for the Settings → Keys panel (SettingsApp.tsx). No DOM, no
// imports — unit-tested via node --test, same split as net-http.mjs and
// settings.mjs (those files' own header comments explain why: the
// React/DOM host can't be imported outside a browser, so the logic worth a
// fast regression test lives here instead).
// KEEP IN SYNC with ./settings-keys.d.mts, which hand-mirrors these
// signatures for the strict shell tsconfig (no allowJs).

/**
 * Normalize `GET /api/keys`'s response body into the row list the panel
 * renders. The route never fails soft with a non-array `credentials` in
 * practice, but a malformed/absent field here must render an empty list, not
 * throw and blank the whole panel.
 *
 * @param {{ credentials?: Array<{ id: string, label: string, help: string, scope: "user"|"user-or-system", set: boolean, systemSet?: boolean }> }} body
 * @returns {Array<{ id: string, label: string, help: string, scope: "user"|"user-or-system", set: boolean, systemSet?: boolean }>}
 */
export function keyRowsFromKeysResponse(body) {
  return Array.isArray(body?.credentials) ? body.credentials : [];
}

/**
 * The masked state shown for a row — NEVER the value itself, which the API
 * never returns in the first place. `set` is the only signal there is.
 *
 * @param {{ set: boolean }} row
 * @returns {string}
 */
export function maskedKeyState(row) {
  return row?.set ? "••••••••" : "Not set";
}

/**
 * What the admin system-key column looks like for one row.
 *
 * A `scope: "user"` credential has no system-wide fallback at all — the
 * column must render nothing there (not a disabled field, which invites "how
 * do I enable this?"), just a one-line note that the app uses the caller's
 * own key only. A `scope: "user-or-system"` row gets the admin control, but
 * only for an actual admin; a non-admin viewer of a user-or-system row gets
 * neither the control nor the note (the note would be false for them — an
 * admin may in fact have set an OS-wide fallback they benefit from).
 *
 * @param {{ scope: "user" | "user-or-system" }} row
 * @param {boolean} isAdmin
 * @returns {{ showSystemControl: boolean, showUserOnlyNote: boolean }}
 */
export function keyRowControls(row, isAdmin) {
  const scope = row?.scope;
  return {
    showSystemControl: isAdmin === true && scope === "user-or-system",
    showUserOnlyNote: scope === "user",
  };
}

/**
 * Read a `ctx.keys.prompt(id)` launch straight out of launch params:
 * `{ panel: "keys", credential: <id> }`. Returns null for anything else
 * (a plain launch, a different panel, a malformed credential) so the caller
 * can no-op instead of switching sections on garbage.
 *
 * @param {Record<string, unknown> | undefined} params
 * @returns {{ credential: string } | null}
 */
export function keysLaunchIntent(params) {
  if (!params || params.panel !== "keys") return null;
  const credential = params.credential;
  if (typeof credential !== "string" || !credential) return null;
  return { credential };
}
